/**
 * Notification settings.
 *
 * The copy does real work here. iOS shows the permission prompt once and
 * never again — dismiss it and the only way back is the iOS Settings app — so
 * the screen says what the prompt will ask before it asks, and the "denied"
 * state gives directions rather than an apology.
 */
import { useState } from "react";

import { clearLog, formatLog, readLog } from "@/lib/log";
import type { Push } from "@/push/usePush";

/** Swallows a failure the caller has already accounted for. */
function ignore(): void {}

const HOURS = Array.from({ length: 24 }, (_, hour) => hour);

/**
 * What is actually being watched, in the user's terms.
 *
 * The show count is the honest headline: the queue is built by the server on
 * its own schedule, so straight after turning notifications on it is empty and
 * saying "0 episodes queued" would read as a failure rather than as a pass
 * that has not run yet.
 */
function summary({ shows, queued }: { shows: number; queued: number }): string {
  const watching = `Watching ${String(shows)} show${shows === 1 ? "" : "s"} for new episodes.`;
  if (queued === 0) return `${watching} Nothing scheduled yet.`;
  return `${watching} ${String(queued)} episode${queued === 1 ? "" : "s"} coming.`;
}

function label(hour: number): string {
  return `${String(hour).padStart(2, "0")}:00`;
}

function QuietHours({ push }: { push: Push }) {
  const { quietFrom, quietTo } = push.settings;
  return (
    <div className="setting">
      <div className="setting-text">
        <strong>Quiet hours</strong>
        <p>Nothing is sent between these times. Episodes airing overnight stay silent.</p>
      </div>
      <div className="hours">
        <select
          aria-label="Quiet hours start"
          value={quietFrom}
          onChange={(event) => {
            void push.setQuietHours(Number(event.target.value), quietTo);
          }}
        >
          {HOURS.map((hour) => (
            <option key={hour} value={hour}>
              {label(hour)}
            </option>
          ))}
        </select>
        <span className="sub">to</span>
        <select
          aria-label="Quiet hours end"
          value={quietTo}
          onChange={(event) => {
            void push.setQuietHours(quietFrom, Number(event.target.value));
          }}
        >
          {HOURS.map((hour) => (
            <option key={hour} value={hour}>
              {label(hour)}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

function Explainer({ state }: { state: Push["state"] }) {
  if (state === "needs-install") {
    return (
      <p className="notice">
        iPhone only allows notifications in an installed app. Open this in Safari, tap Share, then
        Add to Home Screen, and turn them on from there.
      </p>
    );
  }
  if (state === "unsupported") {
    return <p className="notice">This browser does not support notifications.</p>;
  }
  if (state === "denied") {
    return (
      <p className="notice">
        Notifications are blocked. iOS only asks once, so turning them back on means going to
        Settings, finding Up Next in the app list, and allowing notifications there.
      </p>
    );
  }
  return null;
}

/**
 * Handing the log over.
 *
 * Share sheet first, because on an installed iOS app that is the only route
 * that reaches another app. The clipboard is the fallback, and the text is
 * shown either way so it can be selected by hand if both are refused.
 */
function DebugLog() {
  const [text, setText] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    const entries = await readLog();
    const formatted = formatLog(entries);
    setText(formatted);
    setCopied(false);
    if (typeof navigator.share === "function") {
      await navigator.share({ title: "tv-tracker log", text: formatted }).catch(ignore);
      return;
    }
    await navigator.clipboard?.writeText(formatted).then(() => {
      setCopied(true);
    }, ignore);
  };

  return (
    <div className="setting log-setting">
      <div className="setting-text">
        <strong>Diagnostic log</strong>
        <p>
          {copied
            ? "Copied. Paste it into the chat."
            : "What the app tried and what came back. No titles, no account details."}
        </p>
        {text !== null && <textarea className="log-text" readOnly value={text} rows={8} />}
      </div>
      <div className="log-actions">
        <button
          type="button"
          className="quiet"
          onClick={() => {
            void load();
          }}
        >
          Export
        </button>
        {text !== null && (
          <button
            type="button"
            className="foot-link"
            onClick={() => {
              void clearLog().then(() => {
                setText(null);
              });
            }}
          >
            Clear
          </button>
        )}
      </div>
    </div>
  );
}

export function Settings({ push, onClose }: { push: Push; onClose: () => void }) {
  const blocked =
    push.state === "denied" || push.state === "unsupported" || push.state === "needs-install";
  const busy = push.state === "working" || push.state === "loading";

  return (
    <div className="settings">
      <header className="settings-head">
        <h2>Notifications</h2>
        <button type="button" className="quiet" onClick={onClose}>
          Done
        </button>
      </header>

      <Explainer state={push.state} />

      {!blocked && (
        <>
          <div className="setting">
            <div className="setting-text">
              <strong>An hour before an episode airs</strong>
              <p>
                {push.state === "on"
                  ? summary(push.counts)
                  : "iPhone will ask you to allow notifications. It only asks once."}
              </p>
            </div>
            <button
              type="button"
              className={push.state === "on" ? "quiet" : "primary"}
              disabled={busy}
              onClick={() => {
                void (push.state === "on" ? push.disable() : push.enable());
              }}
            >
              {busy ? "…" : push.state === "on" ? "Turn off" : "Turn on"}
            </button>
          </div>

          {push.state === "on" && <QuietHours push={push} />}
        </>
      )}

      {push.error !== null && <p className="notice error">{push.error}</p>}

      <DebugLog />

      <p className="settings-foot">
        Notifications come from a small server that watches these shows for new episodes. It holds a
        list of titles and nothing else — no Simkl password or token is stored there.
      </p>
    </div>
  );
}
